require('./RAContributionPeriodicity.scss');

// @vendors
const React = require('react');
const PropTypes = require('prop-types');
const { times } = require('lodash');
const classNames = require('classnames');

// @commons
const { AmountInput } = require('commonsComponents/amountInput');
const OKSelect = require('components/commons/OKSelect/OKSelect');

// @utilities
const { FormattedText, formatText, getLanguage } = require('core/i18n').i18n;

// @constants
const { EURO_SYMBOL } = require('constants/index');
const {
    CONTRIBUTION_PERIOD_DAILY,
    CONTRIBUTION_PERIOD_WEEKLY,
    CONTRIBUTION_PERIOD_MONTHLY,
    CONTRIBUTION_PERIOD_PUNCTUAL,
    FULL_MONTH_DAYS,
    MAXIMUM_PERIODIC_CONTRIBUTION,
    MAXIMUM_OBJECTIVE_INITIAL_INVESTMENT_AMOUNT,
} = require('../../raConstants/index');

class RAContributionPeriodicity extends React.Component {
    buildPeriodicitySelect(selectedPeriodicity) {
        const label = formatText('raContributionPeriodicity-selectInfo');
        const options = [
            {
                label: formatText('raContributionPeriodicity-daily'),
                value: CONTRIBUTION_PERIOD_DAILY,
            },
            {
                label: formatText('raContributionPeriodicity-weekly'),
                value: CONTRIBUTION_PERIOD_WEEKLY,
            },
            {
                label: formatText('raContributionPeriodicity-monthly'),
                value: CONTRIBUTION_PERIOD_MONTHLY,
            },
        ];

        if (this.props.allowPunctual) {
            options.push({
                label: formatText('raContributionPeriodicity-punctual'),
                value: CONTRIBUTION_PERIOD_PUNCTUAL,
            });
        }

        return (
            <div className="ra-contribution-periodicity__select">
                <OKSelect
                    label={label}
                    name="ra-contribution-periodicity"
                    options={options}
                    value={selectedPeriodicity}
                    onChange={this.props.onPeriodicityChange}
                    clearable={false}
                    searchable={false}
                    disabled={this.props.disableSelect}
                />
                {this.buildSelectedPeriodicityCopy(
                    this.props.selectedPeriodicity,
                )}
            </div>
        );
    }

    getDayClassName(day, selectedValue, isAmountValid) {
        const customClass = classNames('ra-contribution-periodicity__day', {
            ['ra-contribution-periodicity__day--selected']:
                selectedValue === day,
            ['ra-contribution-periodicity__day--disabled']: !isAmountValid,
        });

        return customClass;
    }

    buildSelectedPeriodicityCopy(selectedPeriodicity) {
        if (selectedPeriodicity !== CONTRIBUTION_PERIOD_DAILY) {
            return null;
        } else {
            return (
                <div className="ra-contribution-periodicity__select-copy">
                    <FormattedText value="raContributionPeriodicity-periodicityCopy" />
                </div>
            );
        }
    }

    buildCustomCalendar(selectedDay, amount) {
        const isAmountValid = amount >= 1;
        const handleDayClick = isAmountValid ? this.props.onDayClick : () => {};
        const days = times(FULL_MONTH_DAYS, index => {
            const day = index + 1;
            const className = this.getDayClassName(
                day,
                selectedDay,
                isAmountValid,
            );
            return (
                <div
                    key={day}
                    data-id={day}
                    onClick={handleDayClick}
                    className={className}
                >
                    {day}
                </div>
            );
        });

        return (
            <div className="ra-contribution-periodicity__calendar">
                <label className="ra-contribution-periodicity__calendar-label">
                    <FormattedText value="raContributionPeriodicity-monthCalendar" />
                </label>
                {days}
            </div>
        );
    }

    render() {
        const {
            amount,
            selectedDay,
            selectedPeriodicity,
            errorState,
            errorMessage,
            disabled,
        } = this.props;
        const periodicitySelect = this.buildPeriodicitySelect(
            selectedPeriodicity,
        );
        let monthlyCalendar;
        if (selectedPeriodicity === CONTRIBUTION_PERIOD_MONTHLY) {
            if (!disabled) {
                monthlyCalendar = this.buildCustomCalendar(selectedDay, amount);
            } else {
                monthlyCalendar = (
                    <FormattedText
                        className="ra-contribution-periodicity__no-calendar-text"
                        value="raAssociateObjective-raAssociateObjective_initialContributionInfo"
                    />
                );
            }
        }
        const max =
            selectedPeriodicity === CONTRIBUTION_PERIOD_PUNCTUAL
                ? MAXIMUM_OBJECTIVE_INITIAL_INVESTMENT_AMOUNT
                : MAXIMUM_PERIODIC_CONTRIBUTION;

        return (
            <div className="ra-contribution-periodicity">
                <div className="ra-contribution-periodicity__amount-input">
                    <AmountInput
                        disabled={disabled}
                        amount={amount}
                        label={formatText(
                            'raContributionPeriodicity-contributionAmount',
                        )}
                        fluid={true}
                        max={max}
                        min={0}
                        error={errorState}
                        errorMsg={errorMessage}
                        language={getLanguage()}
                        callback={this.props.onRecurringContributionChange} // eslint-disable-line
                        keepZeroAndEmpty={this.props.keepZeroAndEmpty}
                        hideFocusZero={this.props.hideFocusZero}
                        showBlurZero={false}
                    />
                    <span className="ra-contribution-periodicity__amount-input-symbol">
                        {EURO_SYMBOL}
                    </span>
                </div>
                {periodicitySelect}
                {monthlyCalendar}
            </div>
        );
    }
}

RAContributionPeriodicity.defaultProps = {
    allowPunctual: false,
    disableSelect: false,
    disabled: false,
};

RAContributionPeriodicity.propTypes = {
    allowPunctual: PropTypes.bool,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
    disableSelect: PropTypes.bool,
    errorState: PropTypes.bool,
    errorMessage: PropTypes.string,
    selectedDay: PropTypes.number,
    selectedPeriodicity: PropTypes.number,
    onRecurringContributionChange: PropTypes.func.isRequired,
    onDayClick: PropTypes.func.isRequired,
    onPeriodicityChange: PropTypes.func.isRequired,
    keepZeroAndEmpty: PropTypes.bool,
    hideFocusZero: PropTypes.bool,
};

module.exports = RAContributionPeriodicity;
